import { useSubjectId } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { DetailPageLayout } from '@/layout/components/detail-page-layout/DetailPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { AgendaContactSection } from '@/modules/shared/components/agenda-detail/AgendaContactSection'
import { AgendaDetail } from '@/modules/shared/components/agenda-detail/AgendaDetail'
import { AgendaMoment } from '@/modules/shared/components/agenda-detail/AgendaMoment'
import { AgendaNotes } from '@/modules/shared/components/agenda-detail/AgendaNotes'
import { AgendaLocationCard } from '@/modules/shared/components/agenda-location-card/AgendaLocationCard'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { formatAgendaDate } from '@/modules/shared/helpers/agenda-moment'
import { resolveAgendaContact } from '@/modules/shared/helpers/contact'
import { useNavigateToLocation } from '@/modules/shared/hooks/useNavigateToLocation'
import { RelatedItems } from '@/modules/timeline/components/related-items/RelatedItems'
import { useRelatedItems } from '@/modules/timeline/hooks/useRelatedItems'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import { useMealLink } from '@/utils/navigation'
import { ConfirmAttendanceButton } from '../../components/confirm-attendance-button/ConfirmAttendanceButton'
import { SelectMealCard } from '../../components/select-meal-card/SelectMealCard'
import { useEncounters } from '../../hooks/useEncounters'
import { getEncounterSpecialisation } from '../../utils/encounter-mapping'
import { getEncounterTimeLabel, isFinishedEncounter } from '../../utils/encounter-time'

import type { Encounter } from '../../types/encounter.types'

const EncounterDetailPage = () => {
  const navigate = useNavigate()
  // Hide navigation on native devices
  useHideNativeNavigation()

  const theme = useTheme()
  const { id } = useParams()
  const subjectId = useSubjectId()

  const {
    getEncounterById: {
      execute: getEncounterById,
      value: encounter,
      isLoading: encounterIsLoading,
      error: encounterError,
    },
    confirmEncounterTime: {
      execute: confirmEncounterTime,
      value: confirmEncounterTimeValue,
      isLoading: confirmEncounterTimeLoading,
      reset: resetConfirmEncounterTimeValue,
    },
  } = useEncounters()

  // Finished admissions keep their facts and related items; the actions (meal choice, contact) go,
  // so the meal link, which costs a Synops round trip, is only resolved when the card can show.
  const isPast = encounter ? isFinishedEncounter(encounter) : false
  const mealLink = useMealLink(Boolean(encounter) && !isPast)?.url
  // The section shows only for an item with a trigger; the query keeps the trigger as stored.
  const related = useRelatedItems(encounter?.trigger.trim() ? encounter.trigger : undefined)

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleNavigate = useNavigateToLocation('encounter_location_navigate')

  function handleConfirmTime() {
    if (encounter && subjectId) {
      mixpanel.track('encounter_time_confirm', { origin: 'detail' })
      confirmEncounterTime({
        synopsEncounterId: encounter.id.toString(),
        synopsPatientId: subjectId,
      })
    }
  }

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (id) {
      getEncounterById(Number(id))
      resetConfirmEncounterTimeValue()
    }
  }, [id])

  useEffect(() => {
    if (encounterError && !encounterIsLoading) {
      navigate('/timeline')
    }
  }, [encounterError, encounterIsLoading, navigate])

  // Render Methods
  // ------------------------------------------------------------------------- /
  const header = (
    <DetailPageLayout.Header backLabel={i18n._(globalDefinitions.goBack)} onBack={() => navigate('/timeline')}>
      {encounter ? title(encounter) : null}
    </DetailPageLayout.Header>
  )

  if (encounterIsLoading || !encounter) {
    return (
      <ContentPageLayout backgroundColor={theme.Colors.Background.White} noPadding noScroll>
        <DetailPageLayout>
          {header}
          <DetailPageLayout.Content>{encounterIsLoading ? <AnimatedLoader /> : null}</DetailPageLayout.Content>
        </DetailPageLayout>
      </ContentPageLayout>
    )
  }

  const location = encounter.meta?.location
  const notes = encounter.meta?.notes ?? []
  // Synops does not always honour the type; without a start there is no date or hour to show.
  const dateString = encounter.start ? formatAgendaDate(encounter.start) : undefined
  const timeLabel = getEncounterTimeLabel(encounter)

  const { name: contactName, phone: contactPhone } = resolveAgendaContact(location)

  return (
    <ContentPageLayout backgroundColor={theme.Colors.Background.White} noPadding noScroll>
      <DetailPageLayout>
        {header}

        <DetailPageLayout.Content>
          <AgendaDetail>
            <DetailPageLayout.Title>{title(encounter)}</DetailPageLayout.Title>
            {/* Day - Time - Room. The unknown hour is the one row that is highlighted. */}
            <AgendaMoment date={dateString} time={timeLabel?.message} timeTone={timeLabel?.tone}>
              {location?.room ? (
                <AgendaDetail.Row>
                  <AgendaDetail.Icon icon={['fal', 'door-closed']} />
                  <AgendaDetail.Fact>
                    {t({
                      id: 'encounter.detail.room',
                      message: 'Kamer {room}',
                      values: { room: location.room },
                    })}
                  </AgendaDetail.Fact>
                </AgendaDetail.Row>
              ) : null}
            </AgendaMoment>
            {/* The hospital proposed a check-in time the patient has to confirm; offered only when that time is shown */}
            {timeLabel?.tone === 'warning' ? (
              <ConfirmAttendanceButton
                confirmed={Boolean(confirmEncounterTimeValue)}
                isLoading={confirmEncounterTimeLoading}
                onClick={handleConfirmTime}
              />
            ) : null}

            {/* Location */}
            {location ? (
              <AgendaDetail.Cards>
                <AgendaLocationCard location={location} onNavigate={handleNavigate} />
              </AgendaDetail.Cards>
            ) : null}

            {/* Notes - the design shows the hospital's free text without a heading on an admission */}
            <AgendaNotes notes={notes} />

            {/* Meal choice */}
            {mealLink ? (
              <SelectMealCard href={mealLink} onClick={() => mixpanel.track('appointment_manage_meals')} />
            ) : null}

            {/* Contact */}
            {isPast ? null : (
              <AgendaContactSection
                title={t({
                  id: 'encounters.detail.info',
                  message: 'Meer info over je geplande opname?',
                })}
                description={t({
                  id: 'encounters.detail.contact',
                  comment: 'Contact message for encounter detail',
                  message: 'Neem contact op met je behandelend zorgverlener.',
                })}
                name={contactName}
                phone={contactPhone}
                onCall={() => mixpanel.track('appointment_contact', { type: 'phone', origin: 'detail' })}
              />
            )}

            {/* Related triggers */}
            <RelatedItems {...related} />
          </AgendaDetail>
        </DetailPageLayout.Content>
      </DetailPageLayout>
    </ContentPageLayout>
  )
}

// The phone header and the content from M each show the title, so the page builds it once for both.
function title(encounter: Encounter) {
  const kind = i18n._(activityTypeTranslations.Intake!)
  const specialisation = getEncounterSpecialisation(encounter)

  return (
    <AgendaDetail.Title eyebrow={specialisation ? kind : undefined} eyebrowIcon={['fal', 'house-medical']}>
      {specialisation || kind}
    </AgendaDetail.Title>
  )
}

export { EncounterDetailPage }
